/* Footer and contact section: explicit email-marketing subscriptions to the existing
 * Klaviyo list. Public browser API only; no private key, Shopify duplicate POST,
 * onsite tracking library, historical sync, SMS or open/click-tracking consent.
 * Klaviyo's IP protection + this basic honeypot/cooldown are NOT reCAPTCHA.
 */
(() => {
  if (window.DRFooterNewsletter) return;
  const SELECTOR = 'form[data-newsletter-provider="klaviyo"]:is([data-form-id="newsletter-footer"], [data-form-id="newsletter-contact"])';
  const REVISION = '2026-07-15';
  const COOLDOWN_KEY = 'dr_newsletter_retry_after';
  const instances = new WeakMap();
  const accepted = new WeakSet();
  const now = () => Date.now();
  let retryAfter = 0;
  try { retryAfter = Number(sessionStorage.getItem(COOLDOWN_KEY)) || 0; } catch (error) {}
  function deferRetry(milliseconds) {
    retryAfter = Math.max(retryAfter, now() + milliseconds);
    try { sessionStorage.setItem(COOLDOWN_KEY, String(retryAfter)); } catch (error) {}
  }
  function retryDelay(response) {
    let value = '';
    try { value = response.headers.get('Retry-After') || ''; } catch (error) {}
    const seconds = Number(value);
    if (value.trim() && Number.isFinite(seconds) && seconds >= 0) return Math.max(3000, seconds * 1000);
    const date = Date.parse(value);
    return Number.isFinite(date) ? Math.max(3000, date - now()) : 60000;
  }
  function init(form) {
    if (instances.has(form)) return;
    const email = form.querySelector('[data-newsletter-email]');
    const trap = form.querySelector('[data-newsletter-trap]');
    const button = form.querySelector('[data-newsletter-submit]');
    const status = form.querySelector('[data-newsletter-status]');
    const fallback = form.querySelector('[data-newsletter-fallback]');
    const field = form.querySelector('[data-newsletter-field]');
    if (!email || !trap || !button || !status || !fallback || !field) return;
    const state = { busy: false, timer: null };
    instances.set(form, state);
    const configured = form.dataset.klaviyoSiteId === 'VgtMjp' && form.dataset.klaviyoListId === 'WRguzX';
    function message(text, failed = false) {
      status.setAttribute('role', failed ? 'alert' : 'status');
      status.setAttribute('aria-live', failed ? 'assertive' : 'polite');
      status.textContent = text;
      status.hidden = !text;
    }
    function showFallback() {
      fallback.textContent = 'Use our newsletter sign-up page';
      fallback.hidden = false;
    }
    function refresh() {
      clearTimeout(state.timer);
      button.disabled = state.busy || accepted.has(form) || now() < retryAfter || !configured;
      if (now() < retryAfter && form.isConnected && !accepted.has(form)) {
        state.timer = setTimeout(refresh, Math.min(1000, retryAfter - now()));
      }
    }
    if (!configured || typeof window.fetch !== 'function' || typeof window.AbortController !== 'function') {
      message('Please use our newsletter sign-up page to subscribe.');
      showFallback();
      return;
    }
    email.disabled = false;
    button.hidden = false;
    fallback.hidden = true;
    form.dataset.newsletterReady = 'true';
    if (now() < retryAfter) message('Please wait a moment before trying again.');
    refresh();
    form.addEventListener('submit', async event => {
      event.preventDefault();
      // Own this placement only. Native Shopify customer-form tags/action are
      // absent, so its CAPTCHA/redirect handler must not receive this submit.
      event.stopImmediatePropagation();
      if (state.busy || accepted.has(form)) return;
      if (now() < retryAfter) {
        message('Please wait a moment before trying again.', true);
        refresh();
        return;
      }
      if (trap.value.trim()) {
        deferRetry(3000);
        message('We could not process this request. Please use our newsletter sign-up page.', true);
        showFallback();
        refresh();
        return;
      }
      const address = email.value.trim();
      email.value = address;
      if (!form.reportValidity()) return;
      state.busy = true;
      email.readOnly = true;
      form.setAttribute('aria-busy', 'true');
      fallback.hidden = true;
      message('Submitting…');
      deferRetry(3000);
      refresh();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=VgtMjp', {
          method: 'POST', mode: 'cors', credentials: 'omit', redirect: 'error', referrerPolicy: 'origin',
          headers: { accept: 'application/vnd.api+json', 'content-type': 'application/vnd.api+json', revision: REVISION },
          signal: controller.signal,
          body: JSON.stringify({ data: {
            type: 'subscription',
            attributes: {
              custom_source: form.dataset.formId === 'newsletter-contact'
                ? 'Website contact section (newsletter-contact)' : 'Website footer (newsletter-footer)',
              profile: { data: { type: 'profile', attributes: {
                email: address,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              } } }
            },
            relationships: { list: { data: { type: 'list', id: 'WRguzX' } } }
          } })
        });
        if (controller.signal.aborted) throw new Error('Unconfirmed request');
        // Official endpoint's only successful response is 202 with no body.
        // This is accepted submission, not proof of downstream delivery.
        if (response.status === 202) {
          accepted.add(form);
          field.hidden = true;
          button.hidden = true;
          message('Thank you. Your subscription request has been received.');
          status.focus({ preventScroll: true });
          form.dispatchEvent(new CustomEvent('dr:newsletter:accepted', { bubbles: true, detail: { status: 202 } }));
        } else {
          if (response.status === 429) deferRetry(retryDelay(response));
          message(response.status === 429
            ? 'Please wait a moment before trying again, or use our newsletter sign-up page.'
            : 'We could not confirm your subscription. Please try again or use our newsletter sign-up page.', true);
          showFallback();
        }
      } catch (error) {
        // A timeout/network failure may have occurred after acceptance. Never
        // auto-retry or automatically submit a second request via the fallback.
        message('We could not confirm your subscription. Please wait a moment and try again, or use our newsletter sign-up page.', true);
        showFallback();
      } finally {
        clearTimeout(timeout);
        state.busy = false;
        email.readOnly = false;
        form.removeAttribute('aria-busy');
        refresh();
      }
    }, true);
  }
  function scan() { document.querySelectorAll(SELECTOR).forEach(init); }
  window.DRFooterNewsletter = { scan };
  document.addEventListener('shopify:section:load', scan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
